import { firmStorageUrl } from "../utils";

/**
 * One person on a practice firm — the C.V. block exactly as it was filled in on
 * step 1, headed by the role they were declared under.
 *
 * Shared by the applicant's own detail page and the chapter's endorsement view,
 * because both are answering the same question: what did this application
 * actually say? The admin page keeps its own card — it additionally renders the
 * accept/decline state of legacy register-picked partners.
 */

export const FIRM_ROLE_LABEL: Record<string, string> = {
  principal: "Principal Partner",
  partner: "Partner",
  director: "Director",
};

export default function FirmPersonCard({ p, heading }: { p: any; heading?: string }) {
  const cv = firmStorageUrl(p.cv_file);
  const label = heading ?? FIRM_ROLE_LABEL[p.role] ?? p.role;

  // Identifiers worth seeing at a glance, skipping whatever was left blank.
  const meta = [
    p.previous_name && `previously ${p.previous_name}`,
    p.age && `age ${p.age}`,
    p.niqs_membership_no && `NIQS ${p.niqs_membership_no}`,
    p.niqs_election_date && `elected ${String(p.niqs_election_date).slice(0, 10)}`,
    p.qsrbn_reg_no && `QSRBN ${p.qsrbn_reg_no}`,
    p.qsrbn_reg_date && `registered ${String(p.qsrbn_reg_date).slice(0, 10)}`,
  ].filter(Boolean);

  return (
    <div className="py-3 border-b last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {p.full_name || p.member?.surname || "—"}
          </p>
          <p className="text-xs text-indigo-600">{label}</p>
          {meta.length > 0 && <p className="text-xs text-gray-400 mt-0.5">{meta.join(" · ")}</p>}
        </div>
        {cv && (
          <a href={cv} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline shrink-0">
            CV
          </a>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
        {p.academic_qualification && (
          <div><dt className="text-gray-400">Academic qualifications</dt><dd>{p.academic_qualification}</dd></div>
        )}
        {p.professional_qualification && (
          <div><dt className="text-gray-400">Professional qualifications</dt><dd>{p.professional_qualification}</dd></div>
        )}
        {p.post_qualification_experience && (
          <div className="sm:col-span-2">
            <dt className="text-gray-400">Post-qualification experience</dt>
            <dd className="whitespace-pre-wrap">{p.post_qualification_experience}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}
